import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysFtpPoller } from './queryKeys';
import { type FtpPoller } from './types';
import { zodFtpPollerSchema } from './validations';

/**
 * Fetches a FTP poller secret from the API based on the given ID.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/get_ftp_secret_by_id_vadmin_ftp_poller_secret__ftp_id__get
 */
export const fetchFtpPoller = async (ftpPollerId: UUID) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.FTP_POLLER.GET(ftpPollerId), {
        validate: {
          isResponseSensitive: true,
          response: zodFtpPollerSchema,
        },
      })
      .json<FtpPoller>();
  } catch (error) {
    log.error({
      message: 'Error fetching FTP poller secret',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query based custom hook for getting the data for a FTP poller secret with the given ID.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/get_ftp_secret_by_id_vadmin_ftp_poller_secret__ftp_id__get
 */
export const useQueryFtpPoller = (
  ftpPollerId: UUID,
  options?: Omit<UseQueryOptions<FtpPoller>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<FtpPoller>({
    enabled: Boolean(ftpPollerId),
    queryFn: async () => fetchFtpPoller(ftpPollerId),
    queryKey: queryKeysFtpPoller.get(ftpPollerId),
    ...options,
  });
};
